<script lang="ts">
  import { t } from '../i18n/index';
  import { iconFeedback, iconClose } from '../icons/index';
  import { fileToDataUrl, MAX_SHOT_BYTES } from '../features/screenshot';

  // Im PanelFooter eingebunden — User klickt auf „Feedback geben", Form
  // klappt inline auf, Textarea + Submit, POST /v1/feedback an Worker.
  // Source: 'widget'. siteKey + Page-URL + Sprache als Kontext, damit
  // der Admin im Dashboard sehen kann, von welcher Seite das kam.
  //
  // Screenshot: der Besucher lädt SELBST ein Bild hoch (Datei wählen, einfügen
  // oder reinziehen) — wie im Dashboard. Bewusst KEIN Auto-Capture der Seite:
  // einen Screenshot der Besucher-Ansicht einer fremden Seite automatisch zu
  // erstellen wäre datenschutzrechtlich heikel. Der Shot ist also immer etwas,
  // das der Besucher aktiv anhängt — sichtbar und entfernbar.
  let {
    lang = 'en',
    siteKey,
    apiBase,
  }: {
    lang?: string;
    siteKey?: string;
    apiBase?: string;
  } = $props();

  let open = $state(false);
  let message = $state('');
  let sending = $state(false);
  let result: 'success' | 'error' | 'short' | null = $state(null);
  let errorDetail = $state('');

  // ─── Screenshot state ───
  let shot = $state<string | null>(null);
  let processing = $state(false);  // manual file being compressed
  let dragging = $state(false);
  let fileInputEl = $state<HTMLInputElement | null>(null);

  async function onFile(file: File | null | undefined) {
    if (!file || !file.type.startsWith('image/')) return;
    processing = true;
    try {
      const url = await fileToDataUrl(file);
      if (url.length <= MAX_SHOT_BYTES) shot = url;
    } catch {
      /* ignore — visitor can try a different image */
    } finally {
      processing = false;
    }
  }

  function removeShot() {
    shot = null;
  }

  // Paste an image straight from the clipboard (e.g. right after a system
  // screenshot). Only while the form is open.
  $effect(() => {
    if (!open) return;
    const onPaste = (e: ClipboardEvent) => {
      const item = Array.from(e.clipboardData?.items ?? []).find((i) => i.type.startsWith('image/'));
      const f = item?.getAsFile();
      if (f) { e.preventDefault(); void onFile(f); }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  });

  async function submit() {
    if (message.trim().length < 3) {
      result = 'short';
      return;
    }
    sending = true;
    result = null;
    errorDetail = '';
    try {
      const url = `${apiBase || 'https://accessify-api.accessify.workers.dev'}/v1/feedback`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: 'widget',
          message: message.trim(),
          siteKey: siteKey || null,
          screenshot: shot || undefined,
          context: {
            pageUrl: typeof location !== 'undefined' ? location.href.slice(0, 500) : null,
            referrer: typeof document !== 'undefined' ? document.referrer.slice(0, 200) : null,
            language: lang,
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 200) : null,
            viewport: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : null,
          },
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        errorDetail = body?.error || `HTTP ${res.status}`;
        result = 'error';
      } else {
        result = 'success';
        message = '';
        shot = null;
        // Nach 4s wieder zusammenklappen.
        setTimeout(() => { open = false; result = null; }, 4000);
      }
    } catch (e: any) {
      errorDetail = e?.message || '';
      result = 'error';
    } finally {
      sending = false;
    }
  }

  function cancel() {
    open = false;
    message = '';
    result = null;
    errorDetail = '';
    shot = null;
  }
</script>

<div class="accessify-feedback">
  {#if !open}
    <button
      type="button"
      class="accessify-feedback-trigger"
      onclick={() => { open = true; }}
      aria-label={t('widget.feedback', lang)}
    >
      <span class="accessify-feedback-icon" aria-hidden="true">{@html iconFeedback()}</span>
      {t('widget.feedback', lang)}
    </button>
  {:else}
    <div class="accessify-feedback-form" role="group" aria-label={t('widget.feedback.title', lang)}>
      <label class="accessify-feedback-label" for="accessify-feedback-text">
        {t('widget.feedback.title', lang)}
      </label>
      <textarea
        id="accessify-feedback-text"
        class="accessify-feedback-textarea"
        bind:value={message}
        placeholder={t('widget.feedback.placeholder', lang)}
        rows="4"
        disabled={sending}
      ></textarea>

      <!-- Screenshot: Auto-Vorschau (entfernbar) oder eigenes Bild hochladen -->
      <input
        bind:this={fileInputEl}
        type="file"
        accept="image/*"
        class="accessify-shot-input"
        onchange={() => { void onFile(fileInputEl?.files?.[0]); if (fileInputEl) fileInputEl.value = ''; }}
      />
      {#if shot}
        <div class="accessify-shot">
          <img class="accessify-shot-thumb" src={shot} alt="" />
          <div class="accessify-shot-meta">
            <span class="accessify-shot-title">{t('widget.feedback.screenshotAttached', lang)}</span>
            <button type="button" class="accessify-shot-replace" onclick={() => fileInputEl?.click()}>
              {t('widget.feedback.screenshotReplace', lang)}
            </button>
          </div>
          <button
            type="button"
            class="accessify-shot-remove"
            aria-label={t('widget.feedback.screenshotRemove', lang)}
            onclick={removeShot}
          >{@html iconClose()}</button>
        </div>
      {:else if processing}
        <div class="accessify-shot-drop accessify-shot-drop--busy" aria-live="polite">
          <span class="accessify-shot-spinner" aria-hidden="true"></span>
          {t('widget.feedback.screenshotProcessing', lang)}
        </div>
      {:else}
        <button
          type="button"
          class="accessify-shot-drop"
          class:accessify-shot-drop--drag={dragging}
          onclick={() => fileInputEl?.click()}
          ondragover={(e) => { e.preventDefault(); dragging = true; }}
          ondragleave={() => { dragging = false; }}
          ondrop={(e) => { e.preventDefault(); dragging = false; void onFile(e.dataTransfer?.files?.[0]); }}
        >
          <span class="accessify-shot-ico" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M17 8l-5-5-5 5"/><path d="M12 3v12"/>
            </svg>
          </span>
          <span class="accessify-shot-drop-text">
            <span class="accessify-shot-title">{t('widget.feedback.screenshot', lang)}</span>
            <span class="accessify-shot-hint">{t('widget.feedback.screenshotHint', lang)}</span>
          </span>
        </button>
      {/if}

      {#if result === 'success'}
        <div class="accessify-feedback-status accessify-feedback-status--ok" role="status">
          ✓ {t('widget.feedback.thanks', lang)}
        </div>
      {:else if result === 'short'}
        <div class="accessify-feedback-status accessify-feedback-status--warn" role="alert">
          {t('widget.feedback.tooShort', lang)}
        </div>
      {:else if result === 'error'}
        <div class="accessify-feedback-status accessify-feedback-status--err" role="alert">
          {t('widget.feedback.error', lang)}
          {#if errorDetail} <span class="accessify-feedback-detail">— {errorDetail}</span>{/if}
        </div>
      {/if}

      <div class="accessify-shot-privacy">
        {t('widget.feedback.privacy', lang)}{#if shot}{' '}{t('widget.feedback.privacyShot', lang)}{/if}
      </div>

      <div class="accessify-feedback-actions">
        <button type="button" class="accessify-feedback-btn accessify-feedback-btn--secondary" onclick={cancel} disabled={sending}>
          {t('widget.feedback.cancel', lang)}
        </button>
        <button type="button" class="accessify-feedback-btn accessify-feedback-btn--primary" onclick={submit} disabled={sending || message.trim().length < 3}>
          {sending ? '…' : t('widget.feedback.submit', lang)}
        </button>
      </div>
    </div>
  {/if}
</div>
